<template>
  <div>
      <p v-if="error" class="text-danger">{{ errorMsg }}</p>

      <div class="">

          <transition name="fade-in" mode="out-in">

          <div
              v-if="!initialListLoaded || !initialSetupDataLoaded"
              key="isLoadingFirstTime"
              class="min-height-300"
              style="height: 300px;"
          >
              <busy-spinner></busy-spinner>
          </div>

          <div 
              v-else-if="!initialTotalRows"
              key="noEvents"
              class="w-100"
          >
              <div class="p-4 text-muted text-center border rounded-lg" style="background-color: rgba(255,255,255,0.3)">
                  <div>
                      You have no invited Sourcing Events right now.
                  </div>
                  <div class="mt-4">
                      <b-icon icon="inbox" font-scale="4" class="text-secondary-lighter"></b-icon>
                  </div>
              </div>
              
          </div>

          <div
              v-else-if="initialTotalRows"
              key="hasEventsOrLoading"
          >
              <div v-if="initialSetupDataLoaded" class="d-flex gap-3 flex-wrap justify-content-between align-items-end mb-3">  <!-- FILTER AREA -->
                  <base-search-box
                      v-if="isFilterEnabled('search')"
                      :value="currentFilterValues.searchFilter"
                      ref="searchBox"
                      placeholder="Quick Search..."
                      @search="setFilterAndSearch('searchFilter', $event)"
                      @reset="setFilterAndSearch('searchFilter', '')"
                  ></base-search-box>
                  <b-form inline class="ml-auto">
                      <div class="align-self-end">
                          <b-button 
                              variant="soft-primary" 
                              :disabled="!showFilterReset" 
                              class="text-uppercase" 
                              @click="clearAllFilters"
                          >
                              Clear Filters
                          </b-button>
                      </div>
                      <base-filter-select
                          label="Event Type"
                          v-if="isFilterEnabled('rfx_type')"
                          v-model="currentFilterValues.rfxTypeFilter"
                          :reset-value="initialFilterValues.rfxTypeFilter"
                          first-option-text="All Types"
                          :options="setupData.rfx_type_options"
                          @change="getNewList"
                      ></base-filter-select>
                      <base-filter-select
                          label="Response Status"
                          v-if="isFilterEnabled('response_state')"
                          v-model="currentFilterValues.responseStateFilter"
                          :reset-value="initialFilterValues.responseStateFilter"
                          first-option-text="Any Status"
                          :options="setupData.response_state_options"
                          value-field="value"
                          text-field="title"
                          @change="getNewList"
                      ></base-filter-select>  <!-- first-option-text="Any Status" only used when setting blank first option -->
                      <base-filter-select
                          label="Category"
                          v-if="isFilterEnabled('sourcing_category')"
                          v-model="currentFilterValues.categoryFilter"
                          :reset-value="initialFilterValues.categoryFilter"
                          first-option-text="All Categories"
                          :options="setupData.sourcing_category_options"
                          @change="getNewList"
                      ></base-filter-select>
                      <b-form-group label="View As" class="filter-input-group ml-2">
                          <b-button-group>
                              <b-button
                                  v-for="mode in viewModeOptions"
                                  :key="mode.value"
                                  :variant="viewMode === mode.value ? 'secondary' : 'sharp-secondary'"
                                  v-b-tooltip.hover
                                  :title="mode.text"
                                  @click="viewMode = mode.value"
                              >
                                  <b-icon :icon="mode.icon"></b-icon>
                              </b-button>
                          </b-button-group>
                      </b-form-group>
                      <b-button
                          v-b-tooltip.hover title="Refresh List"
                          variant="light"
                          class="ml-2 align-self-end"
                          @click="refreshList(true)"
                      >
                          <b-icon icon="arrow-repeat"></b-icon>
                      </b-button>
                  </b-form>
              </div>

              <div v-if="viewMode === 'table'" key="tableMode" class="">
                  <b-card 
                      id="vendor-rfx-list-table-card"
                      class="shadow rounded-lg"
                      no-body
                  >
                      <b-table 
                          :items="items"
                          :fields="fieldsForDisplay"
                          :busy="loadingList"
                          primary-key="id" 
                          responsive
                          no-local-sorting
                          no-sort-reset
                          :sort-by.sync="sortByField"
                          :sort-desc.sync="sortDesc"
                          @sort-changed="getNewList"
                          hover
                          :empty-text="emptyText"
                          show-empty
                          tbody-class="cells-align-middle"
                          class="no-top-border td-align-middle mb-0"
                          thead-class="overflow-hidden cells-align-middle"
                          head-variant=""
                      >
                          <template #table-busy>
                              <div class="d-flex align-items-center justify-content-center text-secondary my-3">
                                  <div class="">
                                      <b-spinner class="align-middle"></b-spinner>
                                      <strong class="ml-3">Loading Sourcing Events...</strong>
                                  </div>
                              </div>
                          </template>

                          <template #empty="emptyScope">
                              <h6 class="text-center text-muted">{{ emptyScope.emptyText }}</h6>
                          </template>
                          <template #cell(rfx.title)="data">
                              <div class=""> <!-- d-flex gap-3 align-items-center justify-content-between -->
                                  <a :href="data.item.mainUrl" target="_blank" class="">
                                      {{ data.value }}
                                  </a>
                                  <span class="ml-3 text-uppercase font-weight-700 text-gray-500">{{ data.item.rfx.rfx_type }}</span>
                              </div>
                          </template>

                          <template #cell(rfx.sm_state)="data">
                              <status-object-badge 
                                  :status-object="data.item.rfx.state_dict" 
                                  bare
                                  text-uppercase
                                  no-wrap
                                  font-size="0.8rem"
                              ></status-object-badge>
                          </template>

                          <template #cell(sm_state)="data">
                              <div class="text-center">  <!-- d-flex align-items-center gap-3 justify-content-center -->
                                  <status-object-badge 
                                      :status-object="data.item.response.state_dict"
                                      bare
                                      text-uppercase
                                      no-wrap
                                      font-size="0.8rem"
                                  ></status-object-badge>
                                  <div v-if="needsAttentionStatus[data.item.id]" class="mt-1 font-size-70 text-center">
                                      <div 
                                          v-if="needsAttentionStatus[data.item.id].needsAttention" 
                                          key="needsAttn"
                                          v-b-tooltip.hover
                                          :title="needsAttentionStatus[data.item.id].needsAttentionText"
                                          class="font-weight-bold text-danger"
                                      >  <!-- add flasher-enter-active class to draw more attention if needed --> 
                                          <b-icon icon="exclamation-circle-fill" variant="danger" class="mr-1"></b-icon>
                                          NEEDS ATTENTION
                                      </div>
                                      <div v-else-if="needsAttentionStatus[data.item.id].needsAttention === false" key="noNeedsAttn" class="text-success">
                                          <!-- not showing anything if OK -->
                                      </div>
                                      <div v-else-if="needsAttentionStatus[data.item.id].needsAttention === null" key="loadingNeedsAttn" class="text-muted text-uppercase">
                                          Checking status...
                                      </div>
                                  </div>
                              </div>
                          </template>

                          <template #cell(_deadline_)="data">
                              <div 
                                  v-if="data.item.hasDeadline"
                                  class="text-center"
                              >
                                  <div class="">{{ data.item.deadlineFormatted }}</div>
                                  <div class="text-muted">{{ data.item.deadlineTimeUntil }}</div>
                              </div>
                          </template>

                          <template #cell(invitation_date)="data">
                              <div 
                                  v-if="data.item.response.invitation_date"
                                  class="text-center"
                              >
                                  <div class="">{{ data.item.invitationDateFormatted }}</div>
                                  <div class="text-muted">{{ data.item.invitationDateTimeAgo }}</div>
                              </div>
                          </template>

                          <template #cell(actions)="data">
                              <div class="d-flex justify-content-around gap-2 align-items-center">
                                  <b-button
                                      :variant="data.item.mainButton.variant"
                                      size="md"
                                      block
                                      class="text-uppercase py-2 d-flex align-items-center justify-content-center"
                                      :href="data.item.mainButton.href"
                                      target="_blank"
                                  >
                                      <div class="button-text">{{ data.item.mainButton.text }}</div>
                                      <b-icon v-if="data.item.mainButton.icon" :icon="data.item.mainButton.icon" class="ml-3"></b-icon>
                                  </b-button>
                                  <!-- <div class="flex-equal">
                                      <b-button 
                                          :variant="data.item.rfxButton.variant" 
                                          size="md" 
                                          block
                                          class="text-uppercase py-2" 
                                          :href="data.item.rfxButton.href" 
                                          target="_blank"
                                      >
                                          <div class="d-flex justify-content-center align-items-center">
                                              <div class="button-text">{{ data.item.rfxButton.text }}</div>
                                          </div>
                                      </b-button>
                                  </div>
                                  <div v-if="data.item.responseButton.visible" class="flex-equal">
                                      <b-button 
                                          :variant="data.item.responseButton.variant" 
                                          size="md" 
                                          block
                                          class="text-uppercase py-2" 
                                          :href="data.item.responseButton.href" 
                                          target="_blank"
                                          :disabled="data.item.responseButton.disabled"
                                      >
                                          <div class="d-flex justify-content-center align-items-center">
                                              <div class="button-text">{{ data.item.responseButton.text }}</div>
                                          </div>
                                      </b-button>
                                  </div> -->
                              </div>
                          </template>

                      </b-table>

                      <template #footer v-if="!loadingList && totalRows">
                          <base-table-pagination
                              :current-page.sync="currentPage"
                              :per-page.sync="perPage"
                              :total-items="totalRows"
                              :show-per-page="showPerPage"
                              item-name="Sourcing Events"
                              @update:perPage="getNewList"
                              @update:currentPage="getList"
                          ></base-table-pagination>
                      </template>
                  </b-card>
              </div>

              <div v-if="viewMode === 'card'" key="cardMode" class="">

                  <div
                      v-if="loadingList"
                      key="isLoading"
                      class="min-height-200"
                      style="height: 200px;"
                  >
                      <busy-spinner></busy-spinner>
                  </div>

                  <div 
                      v-else 
                      key="hasEventsCardMode"
                      class=""
                  >  <!-- v-if="totalRows" but not needed since if no totalRows, we show empty thing at the top -->
                      <div v-if="!loadingList && totalRows">  <!-- PAGINATION FOR CARD MODE -->
                          <base-table-pagination
                              :current-page.sync="currentPage"
                              :per-page.sync="perPage"
                              :total-items="totalRows"
                              :show-per-page="showPerPage"
                              item-name="Sourcing Events"
                              @update:perPage="getNewList"
                              @update:currentPage="getList"
                          ></base-table-pagination>
                      </div>
                      <div v-if="!loadingList && !totalRows" key="emptyItemCards" class="mt-3 d-flex align-items-center justify-content-center min-height-200">
                          <h6 class="text-muted">{{ emptyText }}</h6>
                      </div>
                      <div v-else key="itemCards" class="mt-3 d-flex flex-wrap gap-5 justify-content-between">  <!-- CARD AREA -->
                          <div
                              v-for="item in items"
                              :key="item.id"
                              class="max-width-400 min-width-350 flex-fill"
                          >  <!-- class="col mb-3" -->
                              <div
                                  class="response-card h-100 border rounded-lg shadow-sm bg-white overflow-hidden d-flex flex-column"
                              >
                                  <div
                                      class="px-3 py-3 response-card-top bg-gray-100"
                                  >
                                      <div class="mb-2 d-flex justify-content-between">
                                          <h5 class="mb-0">{{ item.rfx.title }}</h5>
                                          <div class="text-uppercase font-weight-700 font-size-100 text-gray-400 mb-0">{{ item.rfx.rfx_type }}</div>
                                      </div>
                                      <div class="d-flex align-items-center justify-content-between">
                                          <div>
                                              <status-object-badge 
                                                  :status-object="item.rfx.state_dict" 
                                                  bare
                                                  text-uppercase
                                                  font-size="0.8rem"
                                              ></status-object-badge>
                                          </div>
                                      </div>
                                  </div>
                                  <div class="flex-fill px-3 py-3 response-card-middle d-flex align-items-center justify-content-center">
                                      <div class="h-100 d-flex flex-column gap-4">

                                          <div 
                                              v-if="item.hasDeadline"
                                              class="w-100 d-flex align-items-center"
                                          >  <!-- v-if="hasDeadline(item)" -->
                                              <div class="width-100">
                                                  <div class="font-weight-500 text-uppercase text-muted text-right font-size-70 mb-1">Response<br>Deadline</div>
                                              </div>
                                              <div class="ml-4 width-150">
                                                  <div class="">
                                                      <p class="mb-0">{{ item.deadlineFormatted }}</p>
                                                      <p class="mb-0 text-muted">{{ item.deadlineTimeUntil }}</p>
                                                  </div>
                                              </div>
                                          </div>

                                          <div class="w-100 d-flex align-items-center">
                                              <div class="width-100">
                                                  <div class="font-weight-500 text-uppercase text-muted text-right font-size-70 mb-1">Response<br>Status</div>
                                              </div>
                                              <div class="ml-4 width-150">
                                                  <status-object-badge 
                                                      :status-object="item.response.state_dict"
                                                      bare
                                                      text-uppercase
                                                      font-size="0.8rem"
                                                  ></status-object-badge>

                                                  <div v-if="needsAttentionStatus[item.id]" class="mt-2 font-size-70 text-left">
                                                      <div 
                                                          v-if="needsAttentionStatus[item.id].needsAttention" 
                                                          key="needsAttn"
                                                          v-b-tooltip.hover
                                                          :title="needsAttentionStatus[item.id].needsAttentionText"
                                                          class="font-weight-bold text-danger"
                                                      >  <!-- add flasher-enter-active class to draw more attention if needed --> 
                                                          <b-icon icon="exclamation-circle-fill" variant="danger" class="mr-1"></b-icon>
                                                          NEEDS ATTENTION
                                                      </div>
                                                      <div v-else-if="needsAttentionStatus[item.id].needsAttention === false" key="noNeedsAttn" class="text-success">
                                                          <!-- not showing anything if OK -->
                                                      </div>
                                                      <div v-else-if="needsAttentionStatus[item.id].needsAttention === null" key="loadingNeedsAttn" class="text-muted text-uppercase">
                                                          Checking status...
                                                      </div>
                                                  </div>

                                              </div>
                                          </div>
                                      </div>
                                  </div>
                                  <div
                                      class="d-flex border-top response-card-buttons"
                                  >
                                      <div class="flex-equal p-1">
                                          <b-button 
                                              :variant="item.rfxButton.variant" 
                                              size="md" 
                                              block
                                              class="text-uppercase py-2" 
                                              :href="item.rfxButton.href" 
                                              target="_blank"
                                          >
                                              <div class="d-flex justify-content-center align-items-center">
                                                  <div class="button-text">{{ item.rfxButton.text }}</div>
                                                  <!-- <b-icon icon="chevron-right" class="ml-2"></b-icon> -->
                                              </div>
                                          </b-button>
                                      </div>

                                      <div
                                          class="bg-gray-200"
                                          style="width: 1px;"
                                          v-if="item.responseButton.visible"
                                      ></div>
                                      
                                      <div 
                                          v-if="item.responseButton.visible"
                                          class="flex-equal p-1"
                                      >
                                          <b-button 
                                              :variant="item.responseButton.variant" 
                                              size="md" 
                                              block
                                              class="text-uppercase py-2" 
                                              :href="item.responseButton.href" 
                                              target="_blank"
                                              :disabled="item.responseButton.disabled"
                                          >
                                              <div class="d-flex justify-content-center align-items-center">
                                                  <div class="button-text">{{ item.responseButton.text }}</div>
                                                  <!-- <b-icon icon="chevron-right" class="ml-2"></b-icon> -->
                                              </div>
                                          </b-button>
                                      </div>

                                  </div>
                              </div>
                          </div>
                      </div>
                      
                  </div>
              
              
              </div>
          
          </div>
          </transition>

      </div>

  </div>
</template>

<script>
//import BaseTablePagination from './BaseTablePagination';
import StatusObjectBadge from '../functional-comps/StatusObjectBadge.vue';
//import DateTimeDeadline from './table-cell-comps/DateTimeDeadline';
import isEqual from 'lodash/isEqual';
import { splAxiosBare, suppliosApi } from '../../js/api-service';
import { HumanizeDateTimeHelper, FormatDateTimeLocal, isFutureDate } from '../../js/filters-helpers';
import { RfxResponse } from '../../js/models/rfxResponse';
import { ResponseWithRfx } from '../../js/models/responseWithRfx';
import BaseTablePagination from '../BaseTablePagination.vue';
import BaseSearchBox from '../BaseSearchBox.vue';
import BaseFilterSelect from '../BaseFilterSelect.vue';


export default {
  name: 'PortalDashboardRfxList',
  components: {
      //DateTimeDeadline,
      StatusObjectBadge,
      BaseTablePagination,
      BaseSearchBox,
      BaseFilterSelect,
  },
  mixins: [],
  props: {
      tenantUserPk: {
          type: [String, Number],
          required: true,
      }
  },
  data: function() {
      return {
          errorMsg: 'Error',
          error: false,
          loadingList: false,
          initialListLoaded: false,  // set true once 
          responseList: [],
          items: [],  // will be instances of ResponseWithRfx -- has 'rfx' and 'response' side-by-side
          needsAttentionStatus: {},  // lookup of { responseId: { needs_attention_external, needs_attention_external_text } }
          viewMode: 'table',  // 'card' or 'table' for now 
          viewModeOptions: [
              {text: 'Cards', value: 'card', icon: 'grid-fill'},
              {text: 'List', value: 'table', icon: 'list'},
          ],
          // SORT / PAGINATION
          initialTotalRows: null,  // update this on the first load to a number -- assuming the first load has ALL AVAILABLE TO SEE
          // use initialTotalRows to decide to show 'empty' state -- otherise you'll end up showing 'empty' state just if a filter returns None
          totalRows: 0,  // updated from .count in paginated response
          sortByField: 'invitation_date',  // use field/column 'key' here... but we actually use api_ordering_name in API call
          sortDesc: true,
          currentPage: 1,
          perPage: 10,
          showPerPage: false,
          // FILTERS & GROUPING
          emptyText: 'No items match that filter criteria',  // used for both table and cards, but only when you originally had something in the list... so this means the filters are too restrictive
          showFilters: [  // this was a prop in other table components... for now just hard-coding
              'search', 'rfx_type', 'response_state',
              // 'sourcing_category',  // turning off until people say ok... was not shown before
          ], 
          initialSetupDataLoaded: false,  // set true after first load of setupData, then keep true
          loadingSetupData: false,  // flag for loading setup data
          setupData: {},  // options for various select boxes (rfx_type, sourcing_category, etc) from getSetupData() is called
          currentFilterValues: {},  // set this using Object.assign({}, this.initialFilterValues) during created hook
          initialFilterValues: {},  // we merge this.defaultFilterValues with any presetFilters (prop) in created hook to make this
          defaultFilterValues: {  // Object.assign({}, initialFilterValues) on create, and when you reset
              // don't change these during lifecycle... they are contants
              searchFilter: '',
              rfxTypeFilter: '',  // filter by rfx_type (RFQ, RFP, etc.) - will be the string value
              categoryFilter: null,  // filter by sourcing_category - will be the ID of sourcing_category
              responseStateFilter: null,  // filter by response.sm_state - make sure backend can handle any special ones...
          },
          // TABLE
          enabledFields: [  // what we actually show in the table - maybe allow customizing per tenant later
              'rfx.title', 'rfx.sm_state', 'sm_state', '_deadline_', 'invitation_date', 'actions',
          ],
          allFields: [  // ORDER IN TABLE SET BY THIS ARRAY
              {
                  key: 'rfx.title',
                  label: 'Sourcing Event',
                  sortable: true,
                  api_ordering_name: 'rfx__title',  // set this for any sortable field, this is what gets sent to API for ordering=_____
                  class: 'font-weight-bold'
              },{
                  key: 'rfx.sm_state',
                  label: 'Event Status',
                  sortable: false,
                  api_ordering_name: 'rfx__sm_state',  // set this for any sortable field, this is what gets sent to API for ordering=_____
                  tdClass: 'text-center'
              },{
                  key: 'rfx.rfx_type',
                  label: 'Event Type',
                  sortable: true,
                  api_ordering_name: 'rfx__rfx_type',
                  class: 'max-width-80',
              },{
                  key: '_deadline_',  // _deadline_ is just a dummy name to avoid conflicts - set other attributes to things as they should be
                  label: 'Response Deadline',
                  sortable: true,  // just for indicating... not sortable because you have 2 different too confusing -- use Coelesce to make new DB annotation and sort by that
                  formatter: (value, key, item) => {
                      return item.deadlineFormatted;  // also have item.deadlineTimeUntil - show both in cell
                  },
                  api_ordering_name: 'rfx__close_date_planned', 
                  class: 'text-center'
              },{
                  key: 'invitation_date',  // _deadline_ is just a dummy name to avoid conflicts - set other attributes to things as they should be
                  label: 'Invite Date',
                  sortable: true,  // just for indicating... not sortable because you have 2 different too confusing -- use Coelesce to make new DB annotation and sort by that
                  formatter: (value, key, item) => {
                      return item.invitationDateFormatted;  // also have item.invitationDateTimeAgo - show both in cell
                  },
                  api_ordering_name: 'invitation_date', 
                  class: 'text-center'
              },{
                  key: 'sm_state',
                  label: 'Your Response Status',
                  sortable: false,
                  api_ordering_name: 'sm_state',  // if you order by this, the values/titles may not match...
                  class: 'text-center'
              },{
                  key: 'rfx.sourcing_category_name',
                  label: 'Category',
                  sortable: true,
                  api_ordering_name: 'rfx__sourcing_category__name',
                  class: 'text-center max-width-120',
                  tdClass: 'small',
              }, // ADD DATE COLUMNS - invite date? deadline, etc... sort by deadline 
              {
                  key: 'actions',
                  label: 'Actions',
                  sortable: false,
                  class: 'text-center max-width-250',
                  tdClass: 'small',
              },
          ],
      }
  },
  computed: {
      showFilterReset: function() {
          // returns BOOLEAN if any currentFilterValues are different than initialFilterValues
          let isSame = isEqual(this.currentFilterValues, this.initialFilterValues);
          return !isSame;
      },
      fieldsForDisplay() {
          return this.allFields.filter(f => this.enabledFields.includes(f.key));
      },
  },
  created() {
      // use this.defaultFilterValues to make this.initialFilterValues
      this.initialFilterValues = Object.assign({}, this.defaultFilterValues);
      this.resetFilterValues();
  },
  async mounted() {
      this.getSetupData();  // just add await to this if you need it before the list is loaded - but really only needed for filters
      this.getList();
  },
  methods: {
      handleError: function(error, message) {
          console.log(message);
          console.log(error);
          this.errorMsg = message;
          this.error = true;
      },
      refreshList: function(noFilterReset = false) {
          // this is one of the only places (THE only?) where we also clear the needsAttentionStatus
          this.clearNeedsAttentionStatus();
          if (noFilterReset) {
              this.getNewList();  // if you specifically set noFilterReset to true (first positional arg), then don't reset filters (but do reset current page to 1)
          } else {
              this.clearAllFilters();   // this resets all filter values and reloads the list
          }
      },
      getNewList() {
          // call getList(), but must reset currentPage first
          // call this from any change to filters, sort, search, etc
          this.currentPage = 1;
          this.$nextTick(() => this.getList());
      },
      getList() {
          this.loadingList = true;
          const axiosConfig = {params: this.getListQueryParams()};
          splAxiosBare.get('/portal/api/user-rfx-responses/', axiosConfig)
          .then(response => {
              console.log('got rfx response list, response below');
              console.log(response);
              // NOW PAGINATED - Nov 11 2022
              const respListRaw = response.data.results;
              this.totalRows = response.data.count;
              if (this.initialTotalRows === null) {
                  // then this is the very first load -- set it to whatever our totalRows is, then don't update again, unless you reset all
                  this.initialTotalRows = this.totalRows;
              }
              this.responseList = respListRaw.map(resp => {
                  return new RfxResponse(resp);
              });
              this.items = respListRaw.map(resp => {
                  // pull out the Rfx from the response, so they aren't nested
                  return new ResponseWithRfx({rfx: resp.rfx, response: resp });
              });
          })
          .catch(error => {
              this.handleError(error, 'Error while getting RFX responses');
          })
          .finally(() => {
              // now fire off individual requests, but don't await them, don't chain promise for them
              this.items.forEach(item => {
                  // we only need to check if response.is_submitted -- otherwise no need to check
                  // populate the lookup first -- but with null values -- will get updated when individual requests are done
                  // NOTE - we ONLY populate it if it is NOT ALREADY THERE AND if response.is_submitted
                  // because we might just be flipping through pages or changing filters, and we don't need to clear the validation status each time we do that
                  const isSubmitted = item.response.is_submitted;
                  const respId = item.response.id;
                  if (respId in this.needsAttentionStatus) {
                      // already there... no need to set it to null and fetch again 
                      console.log(`we already have ${respId} in needsAttentionStatus object - not resetting it`);
                  } else if (isSubmitted) {
                      // if not already in our store, but IS SUBMITTED, then we need to check
                      // add it to store with values that mean 'checking now', and start fetching
                      this.setNeedsAttentionStatus(respId, null, '');
                      this.fetchNeedsAttentionStatus(respId);
                  } else {
                      // if NOT submitted, and not already in there, we WILL add it, but set it to explicit false, no fetching
                      this.setNeedsAttentionStatus(respId, false, '');
                  }
              });
              this.loadingList = false;
              this.initialListLoaded = true;
          })
      },
      async getSetupData() {
          this.loadingSetupData = true;
          let resp;
          try {
              resp = await suppliosApi.rfxResponse.vendor_response_list_setup_data();
          } catch (error) {
              this.handleError(error, 'Error while getting setup data');
          }
          this.setupData = Object.assign({}, resp.data);
          this.initialSetupDataLoaded = true;
          this.loadingSetupData = false;
      },

      // FILTERS / SORTING
      getListQueryParams() {
          // axiosConfig['params'] is where we put search/filter params
          const filterParams = {  // grab the current search/filter values
              search: this.currentFilterValues.searchFilter || null,
              rfx__rfx_type: this.currentFilterValues.rfxTypeFilter || null,
              sm_state: this.currentFilterValues.responseStateFilter || null,
              rfx__sourcing_category: this.currentFilterValues.categoryFilter || null,
          };
          const orderingParams = {
              ordering: this.getOrderingFieldString(),
          };
          const paginationParams = {
              page: this.currentPage,
              page_size: this.perPage,
          };
          return Object.assign({}, filterParams, orderingParams, paginationParams);
      },
      resetFilterValues() {
          this.currentFilterValues = Object.assign({}, this.initialFilterValues);
          // new way
          const filterRefs = ['searchBox'];
          filterRefs.forEach(refName => {
              if (this.$refs[refName]) {
                  this.$refs[refName].reset();
              }
          });
      },
      setFilterAndSearch(filterKey, value) {
          // pass in which filter to set and the value - implemented with search box first... add others later
          console.log('filterKey and value', filterKey, value);
          this.currentFilterValues[filterKey] = value;
          this.getNewList();
      },
      clearAllFilters() {
          // clear/reset all filters and search
          this.resetFilterValues();
          this.getNewList();
      },
      isFilterEnabled(filterName) {
          // checks if a specific filter is enabled
          return this.showFilters.includes(filterName);
      },
      getOrderingFieldString() {
          // look up what field name (string) we should use to send to API, in case key here is different than what API should order by
          // useful if you have serializer methods that get values from related objects... you can use double-underscore notation in DRF for ordering
          // also add '-' if sortDesc is true
          const orderingField = this.allFields.find(fld => fld.key === this.sortByField);
          if (!orderingField || !orderingField.api_ordering_name || !orderingField.sortable) {
              return '';
          }
          const apiOrderingName = orderingField.api_ordering_name;
          return (this.sortDesc ? '-' : '') + apiOrderingName;
      },

      // FETCHING RESPONSE STATUS - fetches 'needs_attention_external' status separately, for each item after list has loaded - added Nov 11 2022
      async fetchNeedsAttentionStatus(responseId) {
          console.log(`Starting fetchNeedsAttentionStatus for resp ID ${responseId}`);
          let resp;
          try {
              resp = await suppliosApi.rfxResponse.needs_attention_external_status(responseId);
          } catch (error) {
              // just log errors... this is happening in background 
              console.error(`Error while getting needs_attention_external_status for RfxResponse ID ${responseId}`);
              console.error(error);
          }
          const { needs_attention_external, needs_attention_external_text } = resp.data;
          // stash in our 'needsAttentionStatus' store - we have helper method to do it 
          this.setNeedsAttentionStatus(responseId, needs_attention_external, needs_attention_external_text);
      },
      setNeedsAttentionStatus(responseId, needsAttention, needsAttentionText = '') {
          // needsAttention - boolean - false = not needed / true = needed / null = fetching now
          // needsAttentionText - string - the string explaining why attention is needed - set to '' otherwise
          // call this to set/reset the value of the needsAttentionStatus for a single RfxResponse
          // when first fetching from API, call this to add the response to the lookup, but with value of null for needsAttention
          // then when we fetch the status, call it again with the values 
          const needsAttentionObj = {needsAttention, needsAttentionText};
          this.$set(this.needsAttentionStatus, responseId, needsAttentionObj);
      },
      getNeedsAttentionStatus(responseId) {
          // get the status from our 'store' for easy display in components - normalized into a single status object
          // object returned is {needsAttention, needsAttentionText}
          // needsAttention is false if not needed, true if needed, null if currently fetching
          return this.needsAttentionStatus[responseId];
      },
      clearNeedsAttentionStatus() {
          // clears the ENTIRE needs attention status object
          // normally we should NOT clear it just when changing filters/sorting/pages, or else you'll get many extra expensive requests
          // but you might want to -- since this is basically our local 'cache' of validation status
          this.needsAttentionStatus = {};
      },


      // METHODS FOR INDIVIDUAL ITEMS IN LIST 
      // once you stabilize the schema, you can make a separate component for each Rfx/RfxResponse
      getHumanizedDateTime(isoString) {
          return HumanizeDateTimeHelper(isoString);
      },
      formatDateTimeLocal(isoString) {
          return FormatDateTimeLocal(isoString);
      },
      hasDeadline(item) {
          // return boolean if we have a deadline to show or not, and its in the future
          const maybeDeadline = item.rfx.close_date_to_show;
          console.log(maybeDeadline);
          return !!maybeDeadline && isFutureDate(maybeDeadline);
      },
  }
}
</script>

<style>

</style>
